import type {
  AppearanceModule,
  AppearanceModulesSet,
  AppearanceStyle,
} from '@setplex/tria-api'
import type { Options } from '~/core/flags'
import type { TFunction } from '~/core/i18n'

import { COLOR } from "/app/libraries/arcana/src/vars/index.ts"

import { STATIC_IDENTIFIERS } from '~/shared/appearance'

/** Default modules migrated from i18n keys to backend titles (NF-173.2 MR-1) */
export const DEFAULT_API_TITLE_MODULE_IDS = new Set<string>([
  STATIC_IDENTIFIERS.HOME,
  STATIC_IDENTIFIERS.TV,
  STATIC_IDENTIFIERS.VOD,
  STATIC_IDENTIFIERS.SHOW,
  STATIC_IDENTIFIERS.EPG,
  STATIC_IDENTIFIERS.CATCHUP,
  STATIC_IDENTIFIERS.EVENT,
])

/** True when capability is on and module is in NF-173.2 MR-1 default set */
export function shouldUseAppearanceModuleTitle(
  identifier: string,
  contentLocalizationEnabled: boolean
): boolean {
  return (
    contentLocalizationEnabled && DEFAULT_API_TITLE_MODULE_IDS.has(identifier)
  )
}

/** NavBar / module list title resolution */
export function resolveModuleTitle(
  mod: AppearanceModule,
  t: TFunction,
  contentLocalizationEnabled: boolean
): string {
  if (
    shouldUseAppearanceModuleTitle(mod.identifier, contentLocalizationEnabled)
  ) {
    return mod.title ?? ''
  }
  if (mod.localizedTitleKey) {
    return t(mod.localizedTitleKey)
  }
  return mod.title ?? ''
}

/**
 * Document title for root module pages.
 * When content localization is off, uses explicit fallbackKey (e.g. `live`),
 * not `localizedTitleKey` (`live_navbar`), to preserve existing tab-title behavior.
 */
export function resolveModuleDocumentTitle(
  mod: AppearanceModule | null | undefined,
  t: TFunction,
  contentLocalizationEnabled: boolean,
  identifier: string,
  fallbackKey: string
): string {
  if (
    shouldUseAppearanceModuleTitle(identifier, contentLocalizationEnabled) &&
    mod
  ) {
    return mod.title ?? ''
  }
  return t(fallbackKey)
}

/**
 * Appearance row / shelf title (NF-173.2 AC-1).
 * Default and external rows always resolve via i18n (`localizedTitleKey`).
 * Custom shelves/categories without a key use the API title as-is.
 */
export function resolveAppearanceRowTitle(
  title: string,
  localizedTitleKey: string | undefined,
  t: TFunction
): string {
  return localizedTitleKey ? t(localizedTitleKey) || title : title
}

export const formatColorConfiguration = (style: AppearanceStyle) => {
  if (style.type === 'COLOR') {
    return style.color
  }

  if (style.type === 'GRADIENT') {
    const { angle, list } = style.configuration

    const colors = list.map(
      (colorConfig) => `${colorConfig.color} ${colorConfig.percentage}%`
    )
    return `linear-gradient(${angle}deg, ${colors.join(', ')})`
  }

  // in case of UNSET | IMAGE | undefined
  return null
}

export const formatAppTheme = (
  theme: AppearanceModulesSet
): React.CSSProperties => {
  const primaryColor = formatColorConfiguration(theme.mainStyle)
  const tertiaryColor = formatColorConfiguration(theme.additionalStyle)
  const backgroundColor = formatColorConfiguration(theme.backgroundStyle)
  const secondaryColor = formatColorConfiguration(theme.secondaryStyle)

  return {
    [COLOR.ACCENT_PRIMARY_GRADIENT]: primaryColor,
    [COLOR.BUTTON_ACCENT_PRIMARY]: primaryColor,
    [COLOR.BUTTON_ACCENT_PRIMARY_GRADIENT]: primaryColor,
    [COLOR.CATEGORY_CARD_ACCENT_PRIMARY_GRADIENT]: primaryColor,
    [COLOR.ACCENT_TERTIARY]: tertiaryColor,
    [COLOR.BACKGROUND_DARK_APP]: backgroundColor,
    [COLOR.ACCENT_SECONDARY]: secondaryColor,
    [COLOR.PROGRESS_BAR_ACCENT_SECONDARY]: secondaryColor,
  } as React.CSSProperties
}

// modules that used when /appearance api not available
// fallbackFlag - optional property from /auth - fallback visibility
// https://setplexapps.atlassian.net/wiki/spaces/NORA/pages/3808034876/Backward+Compatibility+and+Migration
export const STATIC_MODULES = [
  {
    iconName: 'home',
    identifier: 'home',
    localizedTitleKey: 'home',
    title: 'Home',
    type: 'static',
  },
  {
    fallbackFlag: 'tvEnabled',
    iconName: 'tv',
    identifier: 'tv_channels',
    localizedTitleKey: 'live_navbar',
    title: 'Live',
    type: 'static',
  },
  {
    fallbackFlag: 'vodEnabled',
    iconName: 'discovery',
    identifier: 'movie',
    localizedTitleKey: 'movies',
    title: 'Movies',
    type: 'static',
  },
  {
    fallbackFlag: 'tvShowEnabled',
    iconName: 'series',
    identifier: 'tv_shows',
    localizedTitleKey: 'series',
    title: 'Series',
    type: 'static',
  },
  {
    fallbackFlag: 'catchupEnabled',
    iconName: 'catchups',
    identifier: 'catchups',
    localizedTitleKey: 'catchup_other',
    title: 'Catchups',
    type: 'static',
  },
  {
    fallbackFlag: 'epgEnabled',
    iconName: 'epg',
    identifier: 'epg',
    localizedTitleKey: 'epg',
    title: 'EPG',
    type: 'static',
  },
  {
    fallbackFlag: 'liveEventEnabled',
    iconName: 'activity',
    identifier: 'events',
    localizedTitleKey: 'event_other',
    title: 'Events',
    type: 'static',
  },
] as const

export function getModulesFallback(options: Options): AppearanceModule[] {
  return STATIC_MODULES.filter((mod) =>
    'fallbackFlag' in mod ? options[mod.fallbackFlag] : true
  )
}
